(() => {
  const STORAGE_KEY = 'gv_wishlist_v1';
  const MAX_ITEMS = 100;
  const dialog = document.querySelector('[data-gv-wishlist-dialog]');
  if (!dialog) return;

  const grid = dialog.querySelector('[data-gv-wishlist-list]');
  const empty = dialog.querySelector('[data-gv-wishlist-empty]');
  const error = dialog.querySelector('[data-gv-wishlist-error]');
  let items = [];
  let renderVersion = 0;

  function validItem(value) {
    return value && typeof value.handle === 'string' && /^[a-z0-9-]+$/.test(value.handle)
      && typeof value.url === 'string' && value.url.startsWith('/') && value.url.includes('/products/');
  }

  function readItems() {
    try {
      const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
      return Array.isArray(saved) ? saved.filter(validItem).slice(-MAX_ITEMS) : [];
    } catch {
      return [];
    }
  }

  function saveItems() {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
      error.hidden = true;
    } catch {
      error.textContent = dialog.dataset.gvLabelStorageError || '';
      error.hidden = false;
    }
  }

  function updateButtons() {
    const selected = new Set(items.map((item) => item.handle));
    document.querySelectorAll('[data-gv-wishlist-toggle]').forEach((button) => {
      const isSelected = selected.has(button.dataset.gvHandle);
      button.setAttribute('aria-pressed', String(isSelected));
      button.setAttribute('aria-label', isSelected ? button.dataset.gvLabelRemove : button.dataset.gvLabelAdd);
      const label = button.querySelector('[data-gv-wishlist-toggle-text]');
      if (label) label.textContent = isSelected ? button.dataset.gvTextSaved : button.dataset.gvTextSave;
    });
    document.querySelectorAll('[data-gv-wishlist-count]').forEach((counter) => {
      counter.textContent = String(items.length);
      counter.hidden = items.length === 0;
    });
  }

  function itemFromButton(button) {
    return {
      handle: button.dataset.gvHandle,
      title: button.dataset.gvTitle || '',
      url: button.dataset.gvUrl || '',
      image: button.dataset.gvImage || '',
      price: button.dataset.gvPrice || '',
      onRequest: button.dataset.gvOnRequest === 'true',
      available: button.dataset.gvAvailable !== 'false'
    };
  }

  function removeItem(handle) {
    items = items.filter((item) => item.handle !== handle);
    saveItems();
    updateButtons();
    if (dialog.open) renderItems();
  }

  function toggleItem(button) {
    const item = itemFromButton(button);
    if (!validItem(item)) return;
    if (items.some((saved) => saved.handle === item.handle)) {
      removeItem(item.handle);
      return;
    }
    items = items.filter((saved) => saved.handle !== item.handle).slice(-(MAX_ITEMS - 1));
    items.push(item);
    saveItems();
    updateButtons();
    if (dialog.open) renderItems();
  }

  function make(tag, className, content) {
    const el = document.createElement(tag);
    if (className) el.className = className;
    if (content !== undefined) el.textContent = content;
    return el;
  }

  function safeImage(src) {
    try {
      const url = new URL(src, location.origin);
      return url.protocol === 'https:' || url.protocol === 'http:' ? url.href : '';
    } catch {
      return '';
    }
  }

  function drawItem(item) {
    const article = make('article', 'gv-wishlist-item');
    const link = make('a', 'gv-wishlist-item__image');
    link.href = item.url;
    if (item.image && safeImage(item.image)) {
      const image = make('img');
      image.src = safeImage(item.image);
      image.alt = item.title || '';
      image.loading = 'lazy';
      link.appendChild(image);
    }
    article.appendChild(link);

    const description = make('div', 'gv-wishlist-item__details');
    const title = make('a', 'gv-wishlist-item__title', item.title || item.handle);
    title.href = item.url;
    description.appendChild(title);
    const price = make('p', 'gv-wishlist-item__price', item.price || '');
    description.appendChild(price);
    const status = make('p', 'gv-wishlist-item__status');
    if (!item.available && !item.onRequest) {
      status.textContent = dialog.dataset.gvLabelUnavailable || '';
      status.classList.add('gv-wishlist-item__status--unavailable');
    }
    description.appendChild(status);
    const remove = make('button', 'gv-wishlist-item__remove', dialog.dataset.gvLabelRemove || '');
    remove.type = 'button';
    remove.addEventListener('click', () => removeItem(item.handle));
    description.appendChild(remove);
    article.appendChild(description);
    return { article, price, status };
  }

  async function refreshAvailability(item, priceNode, statusNode, version) {
    try {
      const url = new URL(item.url, location.origin);
      if (url.origin !== location.origin || !url.pathname.includes('/products/')) return;
      const response = await fetch(`${url.pathname.replace(/\/$/, '')}.js`, { credentials: 'same-origin' });
      if (version !== renderVersion) return;
      if (response.status === 404) {
        statusNode.textContent = dialog.dataset.gvLabelUnavailable || '';
        statusNode.classList.add('gv-wishlist-item__status--unavailable');
        return;
      }
      if (!response.ok) return;
      const product = await response.json();
      if (version !== renderVersion || !statusNode.isConnected) return;
      if (product.available === false && !item.onRequest) {
        statusNode.textContent = dialog.dataset.gvLabelUnavailable || '';
        statusNode.classList.add('gv-wishlist-item__status--unavailable');
      } else {
        statusNode.textContent = '';
        statusNode.classList.remove('gv-wishlist-item__status--unavailable');
      }
      if (!item.onRequest && Number.isFinite(product.price)) {
        priceNode.textContent = product.price === 0
          ? dialog.dataset.gvLabelOnRequest
          : new Intl.NumberFormat(document.documentElement.lang || 'de-DE', {
              style: 'currency', currency: 'EUR'
            }).format(product.price / 100);
      }
    } catch {
      if (version === renderVersion && item.available !== false && !item.onRequest) {
        statusNode.textContent = dialog.dataset.gvLabelCheck || '';
      }
    }
  }

  function renderItems() {
    renderVersion += 1;
    const version = renderVersion;
    grid.replaceChildren();
    empty.hidden = items.length !== 0;
    grid.hidden = items.length === 0;
    const checks = [];
    items.slice().reverse().forEach((item) => {
      const { article, price, status } = drawItem(item);
      grid.appendChild(article);
      checks.push(() => refreshAvailability(item, price, status, version));
    });
    let next = 0;
    for (let worker = 0; worker < Math.min(4, checks.length); worker += 1) {
      (async () => {
        while (next < checks.length && version === renderVersion) {
          const index = next++;
          await checks[index]();
        }
      })();
    }
  }

  function openDialog() {
    renderItems();
    if (typeof dialog.showModal === 'function') dialog.showModal();
    else dialog.setAttribute('open', '');
  }

  document.addEventListener('click', (event) => {
    const toggle = event.target.closest('[data-gv-wishlist-toggle]');
    if (toggle) {
      event.preventDefault();
      toggleItem(toggle);
      return;
    }
    if (event.target.closest('[data-gv-wishlist-open]')) {
      event.preventDefault();
      openDialog();
      return;
    }
    if (event.target.closest('[data-gv-wishlist-close]')) dialog.close();
  });

  dialog.addEventListener('click', (event) => {
    if (event.target === dialog) dialog.close();
  });

  window.addEventListener('storage', (event) => {
    if (event.key !== STORAGE_KEY) return;
    items = readItems();
    updateButtons();
    if (dialog.open) renderItems();
  });

  const observer = new MutationObserver((records) => {
    const newButtons = records.some((record) => [...record.addedNodes].some((node) =>
      node.nodeType === 1 && (node.matches?.('[data-gv-wishlist-toggle]') || node.querySelector?.('[data-gv-wishlist-toggle]'))
    ));
    if (newButtons) updateButtons();
  });

  items = readItems();
  updateButtons();
  observer.observe(document.body, { childList: true, subtree: true });
})();
